import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewContainerRef
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ReplaySubject } from 'rxjs';
import {
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  tap
} from 'rxjs/operators';
import { CustomerEquipmentService } from '../../../shared/services/customer-equipment.service';
import { CustomerEquipment } from '../../../models/customer-equipment';
import { MatDialog } from '@angular/material/dialog';
import { AddCustomerEquipmentDialogComponent } from '../../../shared/dialogs/add-customer-equipment-dialog/add-customer-equipment-dialog.component';
import { AddWorkshopItemDialogComponent } from '../../../shared/dialogs/add-workshop-item-dialog/add-workshop-item-dialog.component';
import { WorkshopJobListStore } from '../workshop-job-list.store.service';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { REPAIR_DOCKET } from 'src/testIds';
import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { TestIdDirective } from '../../../shared/directives/test-id.directive';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-customer-equipment-select',
    templateUrl: './customer-equipment-select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [CustomerEquipmentService],
    standalone: true,
    imports: [NgIf, MatFormField, TestIdDirective, MatLabel, MatInput, ReactiveFormsModule, MatAutocompleteTrigger, MatAutocomplete, NgFor, MatOption, AsyncPipe]
})
export class CustomerEquipmentSelectComponent {
  testIds = REPAIR_DOCKET;
  search = new FormControl<string>('');
  noEquipment = true;
  private customerEquipmentSource = new ReplaySubject<number>(1);
  customerEquipment$ = this.customerEquipmentSource.asObservable().pipe(
    switchMap((customerId) => this.customerEquipmentService.getAll(customerId)),
    tap((equipment) => (this.noEquipment = equipment.length === 0)),
    mergeMap((equipment) =>
      this.search.valueChanges.pipe(
        startWith(this.search.value),
        filter((search) => typeof search === 'string'),
        map((search) => {
          if (!search) {
            return equipment;
          }
          return equipment.filter(
            (e) =>
              e.name.toLowerCase().includes(search.toLowerCase()) ||
              e.serial_number === search
          );
        })
      )
    )
  );

  addNewItemOption = {};

  @Input() set customerId(value: number) {
    this.customerEquipmentSource.next(value);
    this._customerId = value;
  }

  private _customerId: number;

  @Input() canAdd = false;

  constructor(
    private customerEquipmentService: CustomerEquipmentService,
    private workshopJobListStore: WorkshopJobListStore,
    private matDialog: MatDialog,
    private viewContainerRef: ViewContainerRef
  ) {}

  addNewEquipment() {
    this.matDialog
      .open(AddCustomerEquipmentDialogComponent, {
        data: { customerId: this._customerId },
        viewContainerRef: this.viewContainerRef
      })
      .afterClosed()
      .subscribe((equipment) => {
        if (equipment) {
          this.customerEquipmentSource.next(this._customerId);
          this.addWorkshopItem(equipment);
        }
      });
  }

  addWorkshopItem(item: CustomerEquipment) {
    this.matDialog.open(AddWorkshopItemDialogComponent, {
      viewContainerRef: this.viewContainerRef,
      data: {
        repairable_type: 'customerEquipment',
        repairable_id: item.id,
        name: item.name,
        onSubmit: (data, onSuccess) =>
          this.workshopJobListStore.addWorkshopJob({ item: data, onSuccess })
      }
    });
  }

  handleOptionSelected($event: MatAutocompleteSelectedEvent) {
    this.search.patchValue('');
    $event.option.deselect();
    if ($event.option.value === this.addNewItemOption) {
      this.addNewEquipment();
    } else {
      this.addWorkshopItem($event.option.value);
    }
  }
}
